import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight, Calendar, Loader2 } from 'lucide-react'
import type { VencimientosDelDia, VencimientoConCliente } from '@/types/vencimientos'
import type { ItemCalendario, ItemsDelDia } from '@/core/types/calendario'
import { estadoEfectivoVencimiento } from '@/lib/vencimientos-utils'
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isToday,
} from 'date-fns'
import { es } from 'date-fns/locale'
import { cn } from '@/lib/utils'

/** Cuántos ítems entran en una celda antes de resumir el resto en "+N más". */
const MAX_ITEMS_VISIBLES = 3

/**
 * Un vencimiento vencido es un `pendiente` con fecha pasada: el `estado` crudo nunca vale
 * 'vencido'. Por eso el color va por `estado_efectivo`, igual que los contadores del grid.
 * El `color` que trae `ItemCalendario` no sirve acá — lo calcula el service con el estado crudo.
 */
const esVencido = (item: ItemCalendario) =>
  item.tipo === 'vencimiento' &&
  estadoEfectivoVencimiento(item.datos_originales as unknown as VencimientoConCliente) === 'vencido'

const colorDeItem = (item: ItemCalendario) => {
  if (item.tipo === 'evento') return 'bg-blue-500'
  if (item.tipo === 'tarea') return 'bg-emerald-500'
  // ORR-331: hueco, no relleno. Es la misma serie que las tareas, pero todavía no existe.
  if (item.tipo === 'tarea_proyectada') return 'border border-emerald-500 bg-transparent'
  return esVencido(item) ? 'bg-red-500' : 'bg-orange-500'
}

/** Lo urgente primero: vencidos, pendientes, eventos, tareas, y al final lo que aún no existe. */
const ordenDeItem = (item: ItemCalendario) => {
  if (item.tipo === 'evento') return 2
  if (item.tipo === 'tarea') return 3
  if (item.tipo === 'tarea_proyectada') return 4
  return esVencido(item) ? 0 : 1
}

interface ChipDia {
  id: string
  titulo: string
  color: string
  /** ORR-331: una ocurrencia que el sistema va a crear, no una fila. Se dibuja punteada. */
  proyectada: boolean
}

/**
 * Los chips de una celda. Salen de la fuente unificada (vencimientos + eventos + tareas), y si esa
 * query falló o todavía no llegó caen a los vencimientos, que viajan en otra query independiente.
 *
 * Sin ese fallback la celda queda habilitada y vacía: `tieneContenido` mira `diaInfo`, así que el
 * día seguiría siendo clickeable mientras la grilla lo muestra sin nada. Antes no se notaba porque
 * los puntitos se pintaban desde `diaInfo`. Hallazgo del review de Codex sobre este mismo PR.
 */
const chipsDelDia = (itemsInfo?: ItemsDelDia, diaInfo?: VencimientosDelDia): ChipDia[] => {
  if (itemsInfo?.items.length) {
    return [...itemsInfo.items]
      .sort((a, b) => ordenDeItem(a) - ordenDeItem(b))
      .map((item) => ({
        id: item.id,
        titulo: item.titulo,
        color: colorDeItem(item),
        proyectada: item.tipo === 'tarea_proyectada',
      }))
  }

  // Mismo formato de título que arma `calendarioService.obtenerItemsPorMes`.
  return (diaInfo?.vencimientos ?? []).map((v, i) => ({
    id: v.id ?? `venc-${i}`,
    titulo: `${v.tipo_impuesto} - ${v.cliente_nombre || 'Sin cliente'}`,
    color: estadoEfectivoVencimiento(v) === 'vencido' ? 'bg-red-500' : 'bg-orange-500',
    proyectada: false,
  }))
}

interface CalendarioMensualProps {
  vencimientosPorDia: VencimientosDelDia[]
  itemsPorDia?: ItemsDelDia[]
  mesActual: Date
  onCambiarMes: (fecha: Date) => void
  onSeleccionarDia: (fecha: Date, vencimientos: VencimientoConCliente[]) => void
  onSeleccionarDiaUnificado?: (fecha: Date, items: ItemsDelDia) => void
  isLoading?: boolean
  isFetching?: boolean
}

export function CalendarioMensual({
  vencimientosPorDia,
  itemsPorDia,
  mesActual,
  onCambiarMes,
  onSeleccionarDia,
  onSeleccionarDiaUnificado,
  isLoading = false,
  isFetching = false,
}: CalendarioMensualProps) {
  const inicioMes = startOfMonth(mesActual)
  const finMes = endOfMonth(mesActual)
  const inicioCalendario = startOfWeek(inicioMes, { weekStartsOn: 1 })
  const finCalendario = endOfWeek(finMes, { weekStartsOn: 1 })

  // Crear mapa de vencimientos por fecha
  const vencimientosMap = new Map<string, VencimientosDelDia>()
  vencimientosPorDia.forEach((v) => {
    vencimientosMap.set(v.fecha, v)
  })

  // Crear mapa de items unificados por fecha
  const itemsMap = new Map<string, ItemsDelDia>()
  itemsPorDia?.forEach((item) => {
    itemsMap.set(item.fecha, item)
  })

  // Generar dias del calendario
  const dias: Date[] = []
  let dia = inicioCalendario
  while (dia <= finCalendario) {
    dias.push(dia)
    dia = addDays(dia, 1)
  }

  // Agrupar en semanas
  const semanas: Date[][] = []
  for (let i = 0; i < dias.length; i += 7) {
    semanas.push(dias.slice(i, i + 7))
  }

  const handleMesAnterior = () => onCambiarMes(subMonths(mesActual, 1))
  const handleMesSiguiente = () => onCambiarMes(addMonths(mesActual, 1))

  const getDiaInfo = (fecha: Date) => {
    const fechaStr = format(fecha, 'yyyy-MM-dd')
    return vencimientosMap.get(fechaStr)
  }

  const getItemsInfo = (fecha: Date) => {
    const fechaStr = format(fecha, 'yyyy-MM-dd')
    return itemsMap.get(fechaStr)
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-4 px-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <Calendar className="h-4 w-4" />
            Calendario Fiscal
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3 pt-0">
          <div className="h-[300px] bg-muted animate-pulse rounded-lg" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-4 px-4">
        <CardTitle className="flex items-center gap-2 text-base">
          <Calendar className="h-4 w-4" />
          Calendario Fiscal
          {isFetching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={handleMesAnterior} disabled={isFetching}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium w-32 text-center capitalize">
            {format(mesActual, 'MMMM yyyy', { locale: es })}
          </span>
          <Button variant="outline" size="icon" onClick={handleMesSiguiente} disabled={isFetching}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="relative px-4 pb-3 pt-0">
        {/* Overlay de carga */}
        {isFetching && (
          <div className="absolute inset-0 bg-background/50 backdrop-blur-[2px] z-10 rounded-lg" />
        )}

        <div className="grid grid-cols-7 gap-1">
          {/* Cabecera de dias de la semana */}
          {['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'].map((dia) => (
            <div
              key={dia}
              className="text-center text-xs font-medium text-muted-foreground py-1"
            >
              {dia}
            </div>
          ))}

          {/* Dias del calendario. ORR-296: los ítems se ven dentro de la fecha, no como puntitos.
              El click sigue abriendo el dialog del día completo — los chips no son botones
              propios a propósito: anidar botones es HTML inválido, y ORR-228 ya definió que el
              detalle se abre desde un solo recuadro, comparta el día o no. */}
          {semanas.map((semana, semanaIndex) =>
            semana.map((fecha, diaIndex) => {
              const diaInfo = getDiaInfo(fecha)
              const itemsInfo = getItemsInfo(fecha)
              const esDelMes = isSameMonth(fecha, mesActual)
              const esHoy = isToday(fecha)
              const chips = chipsDelDia(itemsInfo, diaInfo)
              const tieneVencimientos = diaInfo && (diaInfo.totalPendientes > 0 || diaInfo.totalVencidos > 0)
              const tieneContenido = chips.length > 0 || !!tieneVencimientos
              const visibles = chips.slice(0, MAX_ITEMS_VISIBLES)
              const restantes = chips.length - visibles.length

              return (
                <button
                  key={`${semanaIndex}-${diaIndex}`}
                  className={cn(
                    'flex min-h-[104px] flex-col gap-0.5 rounded-lg p-1.5 text-left transition-colors',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                    tieneContenido ? 'hover:bg-muted/50' : 'cursor-default',
                    !esDelMes && 'text-muted-foreground/50',
                    esHoy && 'bg-primary/10 ring-1 ring-primary'
                  )}
                  onClick={() => {
                    if (itemsInfo && onSeleccionarDiaUnificado) {
                      onSeleccionarDiaUnificado(fecha, itemsInfo)
                    } else if (diaInfo) {
                      onSeleccionarDia(fecha, diaInfo.vencimientos)
                    }
                  }}
                  disabled={!tieneContenido}
                  aria-label={`${format(fecha, "d 'de' MMMM", { locale: es })}${
                    chips.length ? `, ${chips.length} ítems` : ', sin ítems'
                  }`}
                >
                  <span className={cn('px-0.5 text-sm leading-none', esHoy && 'font-semibold text-primary')}>
                    {format(fecha, 'd')}
                  </span>

                  {visibles.map((chip) => (
                    <span
                      key={chip.id}
                      title={chip.proyectada ? `${chip.titulo} — programada, todavía no se creó` : chip.titulo}
                      className={cn(
                        'flex items-center gap-1 rounded px-1 py-0.5 text-[11px] leading-tight',
                        chip.proyectada
                          ? 'border border-dashed border-muted-foreground/40 text-muted-foreground'
                          : 'bg-muted/60'
                      )}
                    >
                      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', chip.color)} />
                      <span className="truncate">{chip.titulo}</span>
                    </span>
                  ))}

                  {restantes > 0 && (
                    <span className="px-1 text-[11px] text-muted-foreground">+{restantes} más</span>
                  )}
                </button>
              )
            })
          )}
        </div>

        {/* Leyenda */}
        <div className="flex items-center justify-center gap-4 mt-2 pt-2 border-t text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-500" />
            Vencidos
          </div>
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-orange-500" />
            Pendientes
          </div>
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-blue-500" />
            Eventos
          </div>
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            Tareas
          </div>
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full border border-emerald-500" />
            Programadas
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
